import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ColumnPrefsContext = createContext(null);

export const useColumnPrefsCtx = () => useContext(ColumnPrefsContext);

export const ColumnPrefsProvider = ({ children }) => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/column-prefs");
      setPrefs(data || {});
    } catch (e) {
      setPrefs({});
    }
  }, []);

  useEffect(() => {
    if (user) load();
    else setPrefs({});
  }, [user, load]);

  const getHidden = useCallback((tableKey) => prefs[tableKey]?.hidden || [], [prefs]);
  const getOrder = useCallback((tableKey) => prefs[tableKey]?.order || [], [prefs]);
  const getWidths = useCallback((tableKey) => prefs[tableKey]?.widths || {}, [prefs]);

  const saveHidden = useCallback(async (tableKey, hidden) => {
    setPrefs((p) => ({ ...p, [tableKey]: { ...(p[tableKey] || {}), hidden } }));
    await api.put(`/column-prefs/${encodeURIComponent(tableKey)}`, { hidden });
  }, []);

  const saveOrder = useCallback(async (tableKey, order) => {
    let hidden = [];
    setPrefs((p) => { hidden = p[tableKey]?.hidden || []; return { ...p, [tableKey]: { ...(p[tableKey] || {}), order } }; });
    await api.put(`/column-prefs/${encodeURIComponent(tableKey)}`, { hidden, order });
  }, []);

  const saveWidth = useCallback(async (tableKey, colKey, px) => {
    let widths = {};
    setPrefs((p) => {
      const cur = p[tableKey] || {};
      widths = { ...(cur.widths || {}) };
      if (px == null) delete widths[colKey]; else widths[colKey] = Math.round(px);
      return { ...p, [tableKey]: { ...cur, widths } };
    });
    await api.put(`/column-prefs/${encodeURIComponent(tableKey)}`, { widths });
  }, []);

  return (
    <ColumnPrefsContext.Provider value={{ prefs, getHidden, getOrder, getWidths, saveHidden, saveOrder, saveWidth }}>
      {children}
    </ColumnPrefsContext.Provider>
  );
};
